import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "baseui/modal";
import { colors, css, fonts, glyphs, icons, mediaQueriesNumbers, useMediaQuery } from "../Styles";
import { useTranslation } from "react-i18next";
import { Button } from "./BaseComponents";
import { ButtonTypes } from "../Definitions";

interface PopupModal_Props {
  isModalOpen?:boolean,
  modalHeader:string,
  modalSubHeader?:string,
  modalHeaderContent?:JSX.Element,
  children?:JSX.Element,
  
  onGoBack?: () => void
  onCloseBtn?: () => void
  onLogoutBtn?: () => void

  overrides?:{
    root?: CSSProperties,
    isPrimaryComponent?:boolean
    background?: string
  }
} 
/**
 * PopupModal
 * @param isModalOpen Determines if the modal is visible.
 * @param modalHeader The main text displayed as the modal's title.
 * @param modalSubHeader Secondary text shown beneath the main title.
 * @param modalHeaderContent Additional JSX content displayed under the header.
 * @param modalHeaderContent 
 * @param children JSX content displayed in the body of the modal.
 * @param onGoBack Callback function triggered when the 'Go Back' button is clicked.
 * @param onCloseBtn Callback function triggered when the 'close' button is clicked.
 * @param onLogoutBtn Callback function triggered when the 'logout' button is clicked.
 * @param overrides Optional styles and configurations for customizing the modal.
 * @param overrides.root CSS properties for the modal's root container.
 * @param overrides.isPrimaryComponent Sets primary (blue bg) or secondary (white bg) styling for the modal children.
 * @param overrides.background Background color for the modal body.
 * @returns A modal component with customizable content, styling, and button actions.
 */
export default function PopupModal({ isModalOpen, modalHeader, modalSubHeader, modalHeaderContent, children, onGoBack, onCloseBtn, onLogoutBtn, overrides }:PopupModal_Props) {
  const {t} = useTranslation()
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`); 
  const [isOpen, setIsOpen] = useState<boolean>(isModalOpen!==undefined?isModalOpen:true)
  const [overflow, setOverflow] = useState<string>("auto");
  const modalBodyRef = useRef<HTMLDivElement>(null); // Reference for window size

  // Handle isModalOpen
  useEffect(() => {
    if (isModalOpen!==undefined) setIsOpen((e) => e!==isModalOpen?isModalOpen:e)
  }, [isModalOpen])

  // #region | Handle window size
  useEffect(() => {
    const handleHeight = () => {
      if (modalBodyRef.current) {
        const modalHeight = modalBodyRef.current.getBoundingClientRect().height;
        const maxHeight = window.innerHeight * 0.7; // 70vh
        setOverflow(modalHeight >= maxHeight ? "scroll" : "auto");
      }
    };
    const handleWidth = () => {
      if (modalBodyRef.current) {
        const modalWidth = modalBodyRef.current.getBoundingClientRect().width;
        const maxWidth = window.innerWidth * 0.7; // 70vh
        setOverflow(modalWidth >= maxWidth ? "scroll" : "auto");
      }
    };

    const handleResize = () => {
      handleHeight()
      handleWidth()
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, modalHeaderContent]);
  // #endregion

  // #region | Header Left and Right Btns
  // Header Go Back Btn function
  function handleGoBackBtnClick() {
    if (onGoBack) {onGoBack()}
      else if (onLogoutBtn) {onLogoutBtn()}
  }

  // Header Close Btn function
  function handleCloseBtnClick() {
    if (onCloseBtn) onCloseBtn()
  }

  // Header Go Back Btn
  const goBackBtn = 
    <div style={{position: "absolute", left: "10px", top: "10px"}}>
      <Button 
        content={onLogoutBtn ? t("buttons.logOutBtn") : t("buttons.goBackBtn")}
        startEnhancer={glyphs.Arrow({rotate:180})}
        buttonType={ButtonTypes.textButton}
        onClick={handleGoBackBtnClick}
      />
    </div>
  
  // Header Close Btn
  const closeBtn =
    <div style={{position: "absolute", right: "10px", top: "10px"}}>
      <Button
        content=""
        startEnhancer={glyphs.Close({})}
        buttonType={ButtonTypes.textButton}
        onClick={handleCloseBtnClick}
      />
    </div>
  // #endregion

  // Styling
  const isPrimary = overrides?.isPrimaryComponent;
  const _root = useMemo<CSSProperties>(() => {
    return Object.assign({}, isPrimary ? {...css.modal, ...css.modal_primary} : {...css.modal, ...css.modal_secondary}, overrides?.root || {});
  }, [isPrimary, overrides?.root])

  const modalBodyBackground = overrides?.background || colors.blueDarkPrimary;

  const modalInner = isPrimary? {...css.modalInner_primary} : {...css.modalInner_secondary, overflow}

  return (
    <Modal 
      isOpen={isOpen}
      closeable={false} // Does not close Modal on backdrop click
      overrides={{
        Root: { style: () => Object.assign({}, { zIndex:  isOpen ? "999999999" : "0" } as CSSProperties) },
        Dialog: { style: () => Object.assign({}, { ..._root } as CSSProperties)}
      }}
    > 
      {onGoBack||onLogoutBtn ? goBackBtn : <></>}
      {onCloseBtn ? closeBtn : <></>}
      <ModalHeader style={{ margin: "0", padding: "0", height: "fit-content", position: "sticky", maxWidth: mobile? "280px" : "auto" }}>        
        <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize0, color: colors.greyLightTertiary, textAlign: "center" }}>{modalHeader}</p>
        <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize3, color: colors.greyLightTertiary, textAlign: "center", marginBottom: "30px" }}>{modalSubHeader}</p>
        <div style={{ marginTop: "10px", marginBottom: "30px" }}>{modalHeaderContent}</div>
      </ModalHeader>
      <ModalBody style={{ ...modalInner, background: modalBodyBackground, maxWidth: mobile? "90vw" : "70vw" }}>
        {children}
      </ModalBody>
      {/* ModalFooter is not a thing in PopupModal */}
    </Modal>
  );
}